import { sql } from "drizzle-orm";
import { index, integer, jsonb, pgTable, timestamp, varchar, text, boolean, uuid } from "drizzle-orm/pg-core";

// Session storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// SPEC-Auth-Users-Roles-Audit — canonical users table.
//
// Replaces the old OIDC-shaped `users` table AND the `approved_emails` table.
// Single source of truth for identity, role, and authorization. See
// ~/Productivity/QAM/foundation/SPEC-Auth-Users-Roles-Audit.md for the full
// rationale (UUID primary key + nullable oidc_sub for Bot + future-proofing
// against Replit-auth swaps).
//
// Role taxonomy (mutually exclusive primary roles):
//   'agent'  — Linda / Joey / Tony / Elly / JZ — QA workers
//   'senior' — Kate / Francesco — oversight, default Senior live feed
//   'admin'  — Sunny — engineering / dev access (does not QA)
//   'bot'    — QAM Bot — system-initiated actions
//
// Orthogonal capability flags:
//   is_admin — admin-CAPABLE users whose primary role is not 'admin'. Sarah
//              is `role='senior', is_admin=true` so she gets the senior live
//              feed but can navigate to admin pages.
//
// SPEC-UserProvisioning (2026-09-02) — `dev` and `slack_user_id` dropped:
// verified unused (dev was never gated on; slack_user_id was written but
// never read). `initials` added — stored, admin-editable, backs the
// initials glyph render sites (see shared/initials.ts, added in Phase 3 of
// that SPEC's rollout).
export const users = pgTable("users", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  name: text("name").notNull(),
  role: text("role").notNull(),
  isAdmin: boolean("is_admin").notNull().default(false),
  initials: text("initials").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  oidcSub: text("oidc_sub").unique(),
  lastLoginAt: timestamp("last_login_at", { withTimezone: true }),
  // SPEC-AgentList-Hardening — persisted Ready/Paused state for the AppBar
  // pill + /queue/next gate. readySetAt advances only on transition (so
  // minutesOnShift reflects "minutes since last flip"), not on every poll.
  isReady: boolean("is_ready").notNull().default(false),
  readySetAt: timestamp("ready_set_at", { withTimezone: true }),
  // Per-user QATT scope — which QA task types this user may check
  // (TASK_CATEGORIES: 1 CookUnity dropoff, 2 B2B, 3 regular, 4 pickup).
  // NULL = unrestricted (the default, and every pre-existing user). A newly
  // trained agent is [1]. Read by server/lib/agent-qatt-scope-gate.ts.
  // Migration: migrations/2026-09-19-users-allowed-qatts.sql.
  allowedQatts: integer("allowed_qatts").array(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type User = typeof users.$inferSelect;
export type UpsertUser = typeof users.$inferInsert;

// SPEC-Auth-Users-Roles-Audit — rollback substrate.
//
// The OLD Replit-OIDC `users` table was renamed to `users_oidc_legacy` at
// migration time so we can revert during the ≥7-day rollback window. Declared
// here so Drizzle's schema-diff (used by Replit's publish flow) recognizes the
// table as intentional. NO application code reads it; the schema is
// permissive (every column nullable except id) because we don't need anything
// stronger than "this table exists." Drop this declaration AND the table in
// the follow-up migration once the rollback window closes (target ~June 4,
// 2026).
export const usersOidcLegacy = pgTable("users_oidc_legacy", {
  id: varchar("id").primaryKey(),
  email: varchar("email"),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

// SPEC role enum — string literals matching the DB CHECK constraint.
export const ROLE_AGENT = "agent" as const;
export const ROLE_SENIOR = "senior" as const;
export const ROLE_ADMIN = "admin" as const;
export const ROLE_BOT = "bot" as const;
// SPEC-TrainingSimulator §3 (D16) — trainees reach /train and /api/simulator/*
// only. Server guard: server/lib/trainee-guard.ts via isApprovedUser.
export const ROLE_TRAINEE = "trainee" as const;

export const ROLE_VALUES = [ROLE_AGENT, ROLE_SENIOR, ROLE_ADMIN, ROLE_BOT, ROLE_TRAINEE] as const;
export type UserRole = (typeof ROLE_VALUES)[number];

export function isValidRole(value: unknown): value is UserRole {
  return typeof value === "string" && (ROLE_VALUES as readonly string[]).includes(value);
}
